import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Download, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ReturnToAppButton from './ReturnToAppButton';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleLogin } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  const [autoLoginComplete, setAutoLoginComplete] = useState(false);
  
  const planName = searchParams.get('plan');
  const duration = searchParams.get('duration');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    handleGuestUserAutoLogin();
  }, []);

  const handleGuestUserAutoLogin = async () => {
    // Check if this is a guest user payment
    const isGuestPayment = localStorage.getItem('guest_payment_success');
    const tempToken = localStorage.getItem('temp_token');
    
    if (isGuestPayment && tempToken) {
      try {
        // Set the token for authentication
        localStorage.setItem('token', tempToken);
        
        // Fetch user data to update context
        const response = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${tempToken}` }
        });
        
        if (response.data) {
          // Update AuthContext with user data
          await handleLogin(response.data, tempToken);
          
          toast.success('Welcome! Please complete your profile');
          
          // Clean up guest payment flags
          localStorage.removeItem('guest_payment_success');
          localStorage.removeItem('temp_token');
          localStorage.removeItem('guest_plan');
          localStorage.removeItem('guest_duration');
          localStorage.removeItem('guest_order_id');
          
          setAutoLoginComplete(true);
        }
      } catch (error) {
        console.error('Auto-login failed:', error);
        toast.error('Please login to continue');
      }
    }
    
    // Fetch invoice
    if (orderId) {
      await fetchInvoice();
    } else {
      setLoading(false);
    }
  };

  const fetchInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/payments/invoice/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setInvoiceData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = (format) => {
    if (!invoiceData) return;
    
    if (format === 'html') {
      const blob = new Blob([invoiceData.invoice_html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${orderId}.html`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else if (format === 'pdf') {
      // Open invoice in new window and trigger print dialog
      const printWindow = window.open('', '_blank');
      printWindow.document.write(invoiceData.invoice_html);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Congratulations! 🎉
          </h1>
          
          <p className="text-xl text-gray-700 mb-6">
            Now you have access to <strong className="text-purple-600">{planName}</strong> for <strong className="text-purple-600">{duration}</strong>
          </p>
          
          <div className="bg-white border-2 border-purple-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Download Your Invoice</h2>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => downloadInvoice('html')}
                className="bg-blue-700 hover:bg-blue-700 text-white"
                disabled={!invoiceData}
              >
                <Download className="h-4 w-4 mr-2" />
                Download HTML
              </Button>
              <Button
                onClick={() => downloadInvoice('pdf')}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={!invoiceData}
              >
                <Download className="h-4 w-4 mr-2" />
                Print PDF
              </Button>
            </div>
          </div>
          
          <div className="space-y-3">
            <ReturnToAppButton />
            <Button
              onClick={() => {
                // For newly registered users, trigger profile completion
                if (autoLoginComplete) {
                  navigate('/');
                  // The App.js handleLogin will automatically show ProfileModal
                  // because completed_questionnaire will be false
                } else {
                  navigate('/');
                }
              }}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white font-semibold py-6 text-lg"
            >
              Continue
            </Button>
            <Button
              onClick={() => navigate('/profile')}
              variant="outline"
              className="w-full"
            >
              View Payment History
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
